import { TelehealthCta } from "@/components/site/TelehealthCta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { faqPageJson } from "@/lib/faq-schema";
import offerOverlay from "@/assets/offer-overlay-sm.webp.asset.json";
import offerOverlay320 from "@/assets/offer-overlay-320.webp.asset.json";
import offerOverlay480 from "@/assets/offer-overlay-480.webp.asset.json";
import { EditableText } from "@/components/site/EditableText";
import { PageSection } from "@/components/site/PageSection";
import { SectionCta } from "@/components/site/SectionCta";
import { medicalWebPageScript } from "@/lib/medical-schema";
import { absoluteUrl } from "@/lib/hreflang";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Weight Loss Procedures in Northern Virginia | Virginia Bariatric" },
      {
        name: "description",
        content:
          "Bariatric surgery in Northern Virginia with Dr. Matthew Fitzer — gastric sleeve, gastric bypass, LAP-BAND, ESG, and intragastric balloon procedures.",
      },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: "Weight Loss Procedures in Northern Virginia | Virginia Bariatric" },
        { property: "og:description", content: "Bariatric surgery in Northern Virginia with Dr. Matthew Fitzer — gastric sleeve, gastric bypass, LAP-BAND, ESG, and intragastric balloon procedures." },
        { property: "og:url", content: "https://virginiabariatric.com/services" },
        { property: "og:type", content: "website" },
        { property: "og:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { property: "og:image:alt", content: "Bariatric surgery at Virginia Bariatric Surgery in Northern Virginia" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg" },
        { name: "twitter:image:alt", content: "Bariatric surgery at Virginia Bariatric Surgery in Northern Virginia" },
        { name: "twitter:title", content: "Weight Loss Procedures in Northern Virginia | Virginia Bariatric" },
        { name: "twitter:description", content: "Bariatric surgery in Northern Virginia with Dr. Matthew Fitzer — gastric sleeve, gastric bypass, LAP-BAND, ESG, and intragastric balloon procedures." },
      ] : []),
    ],
    links: [
      { rel: "canonical", href: absoluteUrl("/services") },
      { rel: "alternate", hrefLang: "en", href: absoluteUrl("/services") },
      { rel: "alternate", hrefLang: "es", href: absoluteUrl("/es/servicios") },
      { rel: "alternate", hrefLang: "x-default", href: absoluteUrl("/services") },
    ],
    scripts: import.meta.env.SSR ? [
      ...medicalWebPageScript("/services"),
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": "https://virginiabariatric.com/services#faq",
          mainEntity: SERVICES_FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ] : [],
  }),
  component: ServicesPage,
});

interface ServicesFaq {
  q: string;
  a: string;
  paragraphs?: string[];
  link?: { text: string; to: string };
}

const SURGERY_OR_MEDICATION_ANSWER = [
  "This is a really important question. To steer a patient to the right option, we need to understand two things: (1) what is the minimum weight loss they would be satisfied with? (2) how well (or poorly) do they respond to weight loss medications?",
  "Based on how we answer those two questions, new considerations come up. If surgery looks like a better choice, then we need to assess their fitness for it. For medicine or surgery, we look at things like medical history and health status, insurance coverage, and logistical factors (how much recovery time you can take, copay for medications, etc.).",
  "Patients with a lower BMI or who want to avoid an operation often start with medication, or sometimes an endoscopy procedure. Patients with a high BMI or diabetes usually get the most benefit from surgery. Dr. Fitzer will review the pro's and con's of all the options with you during a one-on-one consultation. That is how we start the process.",
];

const SERVICES_FAQ: ServicesFaq[] = [
  {
    q: "How do I know whether surgery or weight-loss medication is right for me?",
    a: SURGERY_OR_MEDICATION_ANSWER.join(" "),
    paragraphs: SURGERY_OR_MEDICATION_ANSWER,
  },
  {
    q: "What weight-loss procedures does Dr. Fitzer perform?",
    a: "Dr. Fitzer offers gastric sleeve surgery (sleeve gastrectomy), Roux-en-Y gastric bypass, LAP-BAND adjustable gastric banding, endoscopic sleeve gastroplasty (ESG), the Orbera intragastric balloon, and revisional bariatric surgery. He also manages GLP-1 weight-loss medication such as Zepbound and Wegovy for patients who prefer a non-surgical option.",
  },
  {
    q: "Which weight-loss surgery is the most effective?",
    a: "Gastric sleeve surgery and gastric bypass produce the largest and most durable weight loss. Dr. Fitzer's sleeve patients average about 70% excess weight loss, and bypass results are comparable. Endoscopic and balloon procedures produce less weight loss but require no incisions, and medication results depend on staying on treatment.",
  },
  {
    q: "Am I a candidate for bariatric surgery?",
    a: "Most insurers approve bariatric surgery for patients with a BMI of 40 or higher, or a BMI of 35 or higher with an obesity-related condition such as type 2 diabetes, sleep apnea, or high blood pressure. Self-pay patients with a BMI of 30 or higher may also qualify for endoscopic options.",
    link: { text: "Self-pay patients", to: "/self-pay-bariatric-surgery" },
  },
  {
    q: "Does insurance cover weight-loss procedures in Virginia?",
    a: "Many Virginia plans, including Anthem BCBS, Aetna, Cigna, and Medicare, cover gastric sleeve and gastric bypass surgery when medical criteria are met. Endoscopic procedures, balloons, and weight-loss medication are covered less often. Our team verifies your benefits before you commit to anything.",
  },
];


const PROCEDURES: { id: string; to: string; title: string; body: string; cta: string }[] = [
  { id: "qualifications", to: "/resources/qualifications", title: "Qualifications for Surgery", body: "BMI thresholds, comorbidity criteria, and age guidance to help you understand whether you qualify for bariatric surgery.", cta: "See If You Qualify for Surgery" },
  { id: "sleeve", to: "/services/sleeve", title: "Gastric Sleeve Surgery (Sleeve Gastrectomy)", body: "Removes ~80% of the stomach; the most popular procedure, with ~70% excess weight loss.", cta: "Learn About Gastric Sleeve Surgery" },
  { id: "bypass", to: "/services/bypass", title: "Gastric Bypass", body: "Laparoscopic Roux-en-Y bypass — the long-standing gold standard for sustained weight loss and reflux relief.", cta: "Learn About Gastric Bypass" },
  { id: "bypass-vs-sleeve", to: "/services/bypass-vs-sleeve", title: "Gastric Bypass vs. Sleeve", body: "A side-by-side comparison of the two most common operations \u2014 weight loss, diabetes, reflux, safety, and reversibility \u2014 with Dr. Fitzer\u2019s own 10-year data.", cta: "Compare Bypass and Sleeve" },
  { id: "band", to: "/services/band", title: "Adjustable Gastric Banding", body: "The LAP-BAND® procedure places an adjustable silicone band around the upper stomach to limit portion size.", cta: "Learn About the Band" },
  { id: "esg", to: "/services/esg", title: "Endoscopic Sleeve Gastroplasty (ESG)", body: "An incisionless, endoscopic alternative that reshapes the stomach using internal sutures. Same-day outpatient.", cta: "Learn About ESG" },
  { id: "balloon", to: "/services/balloon", title: "Intragastric Balloon", body: "A soft silicone balloon placed in the stomach for up to six months to support short-term weight loss.", cta: "Learn About the Balloon" },
  { id: "revisions", to: "/services/revisions", title: "Revisional Bariatric Surgery", body: "Corrective and conversion procedures for patients with a prior sleeve, bypass, band, or ESG who need a second-stage option.", cta: "Learn About Revisions" },
  { id: "hiatal-hernia", to: "/services/hiatal-hernia-surgery", title: "Hiatal Hernia Repair", body: "Laparoscopic repair of hiatal and paraesophageal hernias — for reflux, regurgitation, and hernias found during weight-loss surgery.", cta: "Learn About Hiatal Hernia Repair" },
  { id: "insurance-coverage", to: "/resources/insurance-coverage", title: "About Bariatric Surgery Insurance Coverage", body: "Which plans cover sleeve, bypass, and revision surgery, the criteria insurers use to approve them, and how our team verifies your benefits.", cta: "Learn About Insurance Coverage" },
  { id: "self-pay", to: "/self-pay-bariatric-surgery", title: "Self-Pay Pricing", body: "Published self-pay prices for sleeve, bypass, Lap-Band, revision, ESG, TORe, and the Orbera balloon \u2014 what is included and how payment works.", cta: "See Self-Pay Pricing" },
];

function ServicesPage() {
  return (
    <>
      <PageSection className="pt-8 pb-6 text-center">
        <EditableText as="h1" contentKey="services.hero.title" defaultValue="Bariatric Surgery in Northern Virginia" className="text-4xl md:text-5xl font-serif" />
        <EditableText as="p" contentKey="services.hero.body" multiline defaultValue="If you are considering weight-loss surgery, it’s important to understand the available procedures and what they can do for you. Dr. Fitzer can help you select the optimal procedure to help you reach your goal." className="mt-6 max-w-2xl mx-auto text-muted-foreground" />
        <TelehealthCta />
      </PageSection>

      <PageSection className="py-6">
        <EditableText as="h2" contentKey="services.explore.title" defaultValue="Explore Our Bariatric Procedures" className="text-2xl md:text-3xl font-serif text-center text-brand-deep" />
        <EditableText as="p" contentKey="services.explore.intro" multiline defaultValue="Dr. Fitzer performs the full range of weight-loss operations. Each link below opens a detailed guide to help you compare your options:" className="mt-4 max-w-2xl mx-auto text-center text-foreground/80 leading-[1.6]" />
        <p className="mt-3 max-w-2xl mx-auto text-center text-foreground/80 leading-[1.6]">
          Paying without insurance?{" "}
          <Link to="/self-pay-bariatric-surgery" className="text-brand-mid font-medium hover:underline">
            See self-pay prices for every procedure
          </Link>
          , including what the price covers, or learn about financing options and{" "}
          <Link to="/resources/prosper" className="text-brand-mid font-medium hover:underline">
            payment plans for weight loss surgery
          </Link>.
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl mx-auto">
          <li>
            <Link to="/resources/qualifications" className="text-brand-mid font-medium hover:underline">Qualifications for surgery</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">The BMI and health criteria for weight-loss surgery, and what to do if you don't meet them yet.</span>
          </li>
          <li>
            <Link to="/services/sleeve" className="text-brand-mid font-medium hover:underline">Gastric sleeve surgery (sleeve gastrectomy)</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">Removes ~80% of the stomach; the most popular procedure, with ~70% excess weight loss.</span>
          </li>
          <li>
            <Link to="/services/bypass" className="text-brand-mid font-medium hover:underline">Roux-en-Y gastric bypass surgery</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">The gold-standard operation for durable weight loss and acid reflux relief.</span>
          </li>
          <li>
            <Link to="/services/bypass-vs-sleeve" className="text-brand-mid font-medium hover:underline">Gastric bypass vs. gastric sleeve comparison</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">Side-by-side breakdown of weight loss, diabetes, reflux, safety, and reversibility.</span>
          </li>
          <li>
            <Link to="/services/band" className="text-brand-mid font-medium hover:underline">LAP-BAND adjustable gastric banding</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">An adjustable silicone band that limits portion size without cutting the stomach.</span>
          </li>
          <li>
            <Link to="/services/esg" className="text-brand-mid font-medium hover:underline">Endoscopic sleeve gastroplasty (ESG)</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">An incisionless, same-day outpatient procedure that reshapes the stomach internally.</span>
          </li>
          <li>
            <Link to="/services/balloon" className="text-brand-mid font-medium hover:underline">Intragastric balloon (Orbera)</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">A temporary gastric balloon for moderate weight loss without an operation.</span>
          </li>
          <li>
            <Link to="/services/revisions" className="text-brand-mid font-medium hover:underline">Revisional bariatric surgery</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">Corrective and conversion procedures for a prior sleeve, bypass, band, or ESG.</span>
          </li>
          <li>
            <Link to="/services/hiatal-hernia-surgery" className="text-brand-mid font-medium hover:underline">Hiatal hernia repair</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">Laparoscopic repair of hiatal and paraesophageal hernias, alone or during weight-loss surgery.</span>
          </li>
          <li>
            <Link to="/resources/insurance-coverage" className="text-brand-mid font-medium hover:underline">About bariatric surgery insurance coverage</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">How insurance approval works for weight-loss surgery, and what our team verifies with your plan before you commit.</span>
          </li>
          <li>
            <Link to="/self-pay-bariatric-surgery" className="text-brand-mid font-medium hover:underline">Self-pay pricing</Link>
            <span className="block text-sm text-muted-foreground leading-[1.6]">Published self-pay prices for sleeve, bypass, Lap-Band, revision, ESG, TORe, and the Orbera balloon.</span>
          </li>
        </ul>
      </PageSection>

      <PageSection fullWidth className="bg-brand-soft/50 py-8" innerClassName="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {PROCEDURES.map((p) => (
          <Link key={p.id} to={p.to} className="group block rounded-2xl border border-border bg-card py-4 px-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl">
            <div className="h-9 w-9 rounded-full bg-brand/10 text-brand grid place-items-center font-serif text-lg">+</div>
            <EditableText as="h2" contentKey={`services.procedure.${p.id}.title`} defaultValue={p.title} className="mt-3 text-lg font-serif text-brand-mid" />
            <EditableText as="p" contentKey={`services.procedure.${p.id}.body`} multiline defaultValue={p.body} className="mt-3 text-sm text-muted-foreground leading-[1.6]" />
            <span className="mt-2.5 inline-block text-sm font-medium text-brand-mid group-hover:translate-x-1 transition">
              <EditableText as="span" contentKey={`services.procedure.${p.id}.cta`} defaultValue={p.cta} />
              <span aria-hidden="true"> →</span>
            </span>
          </Link>
        ))}
      </PageSection>

      <PageSection className="py-6">
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/resources/qualifications"
            preload="intent"
            className="rounded-full bg-brand-logo px-6 py-2.5 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Do I qualify for surgery?
          </Link>
          <Link
            to="/resources/insurance-coverage"
            preload="intent"
            className="rounded-full bg-brand-logo px-6 py-2.5 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Verify surgery benefits
          </Link>
        </div>
      </PageSection>



      <PageSection className="py-5 grid gap-8 md:grid-cols-2 items-center">
        <img
          src={offerOverlay320.url}
          srcSet={`${offerOverlay320.url} 320w, ${offerOverlay480.url} 480w, ${offerOverlay.url} 669w`}
          sizes="(min-width: 768px) min(40vw, 512px), 80vw"
          alt="Overview of bariatric weight-loss options offered at Virginia Bariatric Surgery"
          width={669}
          height={700}
          loading="lazy"
          decoding="async"
          className="w-[80%] mx-auto md:ml-0 md:mr-auto rounded-xl object-cover"
        />
        <div>
          <EditableText as="h2" contentKey="services.choose.title" defaultValue="Understand the Options" className="text-2xl md:text-3xl font-serif" />
          <EditableText as="p" contentKey="services.choose.p1" multiline defaultValue="The right choice depends on your medical status, lifestyle, and — probably more than anything else — your weight-loss goal. In a one-on-one consultation, Dr. Fitzer walks you through the outcome expectations, risks, and recovery (where applicable) for all options for which you are a candidate." className="mt-4 text-foreground/80 leading-[1.6]" />

          <div className="mt-5 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/appointments" className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90 text-center">
              Request a consultation
            </Link>
            <Link to="/" className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90 text-center">
              Back to Homepage
            </Link>
          </div>
        </div>
      </PageSection>

      <PageSection className="py-6">
        <h2 className="text-2xl md:text-3xl font-serif text-center text-brand-deep">Bariatric surgery near Herndon, Reston and Fairfax</h2>
        <p className="mt-3 text-foreground/80 leading-[1.6]">Dr. Fitzer sees patients at our office at 171 Elden Street, Suite 3B, Herndon, and operates at INOVA Fair Oaks Hospital in Fairfax. Endoscopic procedures (ESG, TORe and the Orbera balloon), LAP-BAND surgery and hiatal hernia repair are almost always outpatient. Sleeve, bypass and most revisions usually need one night in the hospital. Call <a href="tel:7037099771" className="text-brand-mid font-medium hover:underline">703-709-9771</a> to schedule.</p>
      </PageSection>

      <PageSection className="py-6">
        <h2 className="text-2xl md:text-3xl font-serif text-center text-brand-deep">Weight-Loss Surgery FAQs</h2>
        <dl className="mt-5 max-w-3xl mx-auto">
          {SERVICES_FAQ.map((f) => {
            const [before, after] = f.link ? f.a.split(f.link.text) : [f.a];
            return (
              <div key={f.q} className="mt-4">
                <dt className="font-semibold text-foreground">{f.q}</dt>
                <dd className="mt-1 text-foreground/80 leading-[1.6]">
                  {f.paragraphs ? (
                    f.paragraphs.map((paragraph) => (
                      <p key={paragraph} className="mt-3 first:mt-0">
                        {paragraph}
                      </p>
                    ))
                  ) : (
                    <>
                      {before}
                      {f.link && after !== undefined && (
                        <Link to={f.link.to} className="text-brand-mid font-medium hover:underline">{f.link.text}</Link>
                      )}
                      {after}
                    </>
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
        <SectionCta to="/appointments" label="cta_services_bottom">
          Discuss your options with Dr. Fitzer
        </SectionCta>
      </PageSection>
    </>

  );
}
